import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  Accordion,
  DateRangeField,
  DropdownField,
  FormValues,
  ModalControlModalProps,
  NakedButton,
  RadioField,
  Text,
  TimeRangeField,
  Tray,
  useForm,
} from '@frontend/design-system';
import { FilterableCheckboxList } from '../../../../components/filterable-checkbox-list';
import { useGetLocationOperatoryProviderList } from '../../../../hooks/scheduler-v3/use-get-location-operatory-provider-list';
import { getLabelWithTimezoneAbbr } from '../../../../utils';
import { CustomAccordionHeader } from '../../../appointment-list-filter-button/filter-drawer/custom-accordion-header';
import { AppliedFilters } from './applied-filters';

export interface FilterState {
  providers: string[];
  operatories: string[];
  serviceTypes: string[];
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  view?: 'CURRENT' | 'EXPIRED';
  recurrenceType?: 'SINGLE_OCCURRENCE' | 'WEEKLY' | 'SPECIFIC_DATE' | 'CUSTOM';
}

export type DetailedFiltersFormConfig = {
  providers: { type: 'multiselect'; value: string[] };
  operatories: { type: 'multiselect'; value: string[] };
  serviceTypes: { type: 'multiselect'; value: string[] };
  dateRange: { type: 'dateRange'; value: [string, string] };
  timeRange: { type: 'timeRange'; value: [string, string]; interval: 15 };
  view: { type: 'dropdown'; value: string };
  recurrenceType: { type: 'dropdown'; value: string };
};

export const DEFAULT_FILTERS: FilterState = {
  providers: [],
  operatories: [],
  serviceTypes: [],
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  view: undefined,
  recurrenceType: undefined,
};

export const toFormValues = (filters: FilterState): FormValues<DetailedFiltersFormConfig> => ({
  providers: filters.providers,
  operatories: filters.operatories,
  serviceTypes: filters.serviceTypes,
  dateRange: [filters.startDate, filters.endDate],
  timeRange: [filters.startTime, filters.endTime],
  view: filters.view ?? 'CURRENT',
  recurrenceType: filters.recurrenceType ?? '',
});

const normalizeFilters = (values: FormValues<DetailedFiltersFormConfig>): FilterState => ({
  providers: values.providers || [],
  operatories: values.operatories || [],
  serviceTypes: values.serviceTypes || [],
  startDate: values.dateRange?.[0] || '',
  endDate: values.dateRange?.[1] || '',
  startTime: values.timeRange?.[0] || '',
  endTime: values.timeRange?.[1] || '',
  view: (values.view || undefined) as FilterState['view'],
  recurrenceType: (values.recurrenceType || undefined) as FilterState['recurrenceType'],
});

const hasActiveFilters = (filters: FilterState) => getActiveFilterCount(filters) > 0;

const getActiveFilterCount = (filters: FilterState) =>
  [
    filters.providers.length > 0,
    filters.operatories.length > 0,
    filters.serviceTypes.length > 0,
    !!filters.startDate,
    !!filters.endDate,
    !!filters.startTime,
    !!filters.endTime,
    !!filters.view && filters.view !== 'CURRENT',
    !!filters.recurrenceType,
  ].filter(Boolean).length;

interface FiltersDrawerProps extends ModalControlModalProps {
  locationId: string;
  filters: FilterState;
  setFilters: Dispatch<SetStateAction<FilterState>>;
  setShowEnabledFiltersBadge?: (show: boolean) => void;
}

export const FiltersDrawer = ({
  locationId,
  filters,
  setFilters,
  setShowEnabledFiltersBadge,
  ...modalProps
}: FiltersDrawerProps) => {
  const { t } = useTranslation('schedule-settings');
  const { locationTimezoneAbbr } = useLocationTimezone(locationId);

  const { providerList, operatoryList } = useGetLocationOperatoryProviderList({
    locationIds: locationId ? [locationId] : [],
  });

  const serviceTypesQuery = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId: locationId,
    pageConfig: { page: 1, limit: 100 },
  });

  const serviceTypeOptions = useMemo(
    () =>
      (serviceTypesQuery.data?.appointmentTypeGroups || [])
        .flatMap((group) => group.bookableAppointmentTypes ?? [])
        .map((service) => ({ value: service.id ?? '', label: service.displayName || '' }))
        .filter((service) => service.value && service.label),
    [serviceTypesQuery.data?.appointmentTypeGroups]
  );

  const { getFieldProps, seedValues, values } = useForm<DetailedFiltersFormConfig, keyof DetailedFiltersFormConfig>({
    fields: {
      providers: { type: 'multiselect', value: filters.providers },
      operatories: { type: 'multiselect', value: filters.operatories },
      serviceTypes: { type: 'multiselect', value: filters.serviceTypes },
      dateRange: { type: 'dateRange', value: [filters.startDate, filters.endDate] },
      timeRange: { type: 'timeRange', value: [filters.startTime, filters.endTime], interval: 15 },
      view: { type: 'dropdown', value: filters.view || 'CURRENT' },
      recurrenceType: { type: 'dropdown', value: filters.recurrenceType ?? '' },
    },
  });

  const normalizedValues = normalizeFilters(values);

  const filterCounts = useMemo(() => {
    return {
      providers: normalizedValues.providers.length,
      operatories: normalizedValues.operatories.length,
      services: normalizedValues.serviceTypes.length,
    };
  }, [normalizedValues]);

  const filterLists = useMemo(() => {
    return {
      providers: providerList,
      operatories: operatoryList,
      services: serviceTypeOptions,
    };
  }, [providerList, operatoryList, serviceTypeOptions]);

  const handleReset = () => {
    seedValues(toFormValues(DEFAULT_FILTERS));
    setFilters(DEFAULT_FILTERS);
    setShowEnabledFiltersBadge?.(false);
  };

  const handleApply = () => {
    setFilters(normalizedValues);
    setShowEnabledFiltersBadge?.(hasActiveFilters(normalizedValues));
    modalProps.onClose?.();
  };

  useEffect(() => {
    seedValues(toFormValues(filters));
  }, [filters, modalProps.show, seedValues]);

  return (
    <Tray {...modalProps} width='smallXXL'>
      <Tray.Header
        Buttons={
          <NakedButton onClick={modalProps.onClose}>
            <Icon name='x' size={24} />
          </NakedButton>
        }
      >
        {t('Filters')}
      </Tray.Header>
      <Tray.Body
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2),
          marginTop: theme.spacing(2),
        }}
      >
        <FilterSectionWrapper title={t('Exception Target')}>
          <Accordion
            variant='main'
            shouldRenderBody
            chevronSize={16}
            css={{ '& > div:last-child': { marginBottom: 0 }, overflowY: 'auto' }}
          >
            <Accordion.Item value='providers'>
              <CustomAccordionHeader title={t('Providers')} icon='pending-small' filterCount={filterCounts.providers} />
              <Accordion.Body>
                <FilterableCheckboxList
                  listStyle={listStyle}
                  fieldName='providers'
                  checkListField={getFieldProps('providers')}
                  list={filterLists.providers}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item value='operatories'>
              <CustomAccordionHeader
                title={t('Operatories')}
                icon='tile-small'
                filterCount={filterCounts.operatories}
              />
              <Accordion.Body>
                <FilterableCheckboxList
                  listStyle={listStyle}
                  fieldName='operatories'
                  list={filterLists.operatories}
                  checkListField={getFieldProps('operatories')}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item value='services'>
              <CustomAccordionHeader title={t('Services')} icon='user-small' filterCount={filterCounts.services} />
              <Accordion.Body>
                <FilterableCheckboxList
                  listStyle={listStyle}
                  fieldName='serviceTypes'
                  list={filterLists.services}
                  checkListField={getFieldProps('serviceTypes')}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </FilterSectionWrapper>
        <FilterSectionWrapper title={t('Time Frame')}>
          <DateRangeField
            {...getFieldProps('dateRange')}
            label={t('Exception Date')}
            labelMin={getLabelWithTimezoneAbbr(t('Start Date'), locationTimezoneAbbr)}
            labelMax={getLabelWithTimezoneAbbr(t('End Date'), locationTimezoneAbbr)}
          />
          <div
            css={css`
              .exception-date-range {
                justify-content: space-between;
              }
              .start-time,
              .end-time {
                width: 170px;
              }
            `}
          >
            <TimeRangeField
              {...getFieldProps('timeRange')}
              label={t('Exception Time')}
              startLabel={getLabelWithTimezoneAbbr(t('Start Time'), locationTimezoneAbbr)}
              endLabel={getLabelWithTimezoneAbbr(t('End Time'), locationTimezoneAbbr)}
              className='exception-date-range'
            />
          </div>
        </FilterSectionWrapper>
        <FilterSectionWrapper title={t('View')}>
          <RadioField {...getFieldProps('view')}>
            <RadioField.Radio value='CURRENT'>{t('Current')}</RadioField.Radio>
            <RadioField.Radio value='EXPIRED'>{t('Expired')}</RadioField.Radio>
          </RadioField>
        </FilterSectionWrapper>
        <FilterSectionWrapper title={t('Recurrence')}>
          <DropdownField label={t('Recurrence')} {...getFieldProps('recurrenceType')}>
            <DropdownField.Option value=''>{t('All')}</DropdownField.Option>
            <DropdownField.Option value='SINGLE_OCCURRENCE'>{t('Single')}</DropdownField.Option>
            <DropdownField.Option value='WEEKLY'>{t('Weekly')}</DropdownField.Option>
            <DropdownField.Option value='CUSTOM'>{t('Custom')}</DropdownField.Option>
            <DropdownField.Option value='SPECIFIC_DATE'>{t('Specific Date')}</DropdownField.Option>
          </DropdownField>
        </FilterSectionWrapper>
      </Tray.Body>
      <AppliedFilters normalizedValues={normalizedValues} filterLists={filterLists} seedValues={seedValues} />
      <Tray.Actions
        primaryLabel={t('Apply')}
        secondaryLabel={t('Reset')}
        onPrimaryClick={handleApply}
        onSecondaryClick={handleReset}
      />
    </Tray>
  );
};

const listStyle = css({
  minHeight: 200,
  maxHeight: 'calc(100vh - 645px)',
  overflowY: 'auto',
});

const FilterSectionWrapper = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <section
    css={{
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${theme.colors.neutral20}`,
    }}
  >
    {title && <Text weight='bold'>{title}</Text>}
    {children}
  </section>
);
